// Serializacao texto-puro <-> HTML simples para o editor de descricao longa (C55 F8).
// KISS: sem lib WYSIWYG (TipTap/Quill sao pesadas demais pra operador nao-tecnico).
// O operador digita texto normal com paragrafos (Enter = nova linha em branco = novo paragrafo).
// No boundary de leitura/gravacao, convertemos texto <-> HTML basico (<p>).
//
// Regra: se o conteudo existente tem HTML alem do que este conversor entende
// (mais que <p>/<br>, ex: <strong>, <ul>, estilos inline vindos de import legado),
// NAO reescrevemos automaticamente — preservamos o HTML original e avisamos o
// operador (isRichHtml = true), para nao perder conteudo.

const SIMPLE_TAGS_RE = /<\/?(p|br)\s*\/?>/gi;
const ANY_TAG_RE = /<[a-z][\s\S]*>/i;

/**
 * Detecta se a string contem HTML alem do que o editor simples entende
 * (qualquer tag que nao seja <p> ou <br>). Nesse caso preservamos o HTML
 * cru em vez de tentar converter (evita perda de conteudo).
 */
export function isRichHtml(value: string): boolean {
  if (!value) return false;
  const withoutSimple = value.replace(SIMPLE_TAGS_RE, "");
  return ANY_TAG_RE.test(withoutSimple);
}

/**
 * Converte HTML simples (<p>...</p> separados, com <br> opcional para quebras
 * de linha dentro do paragrafo) para texto puro que o operador edita:
 * paragrafos viram blocos separados por linha em branco.
 */
export function htmlToPlainText(html: string): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/^<p>/i, "")
    .replace(/<\/p>\s*$/i, "")
    .replace(/<p>/gi, "")
    .replace(/<\/p>/gi, "")
    .trim();
}

/**
 * Converte texto puro digitado pelo operador (paragrafos separados por
 * linha em branco) para HTML basico, no formato que o WooCommerce espera
 * no campo `description`.
 */
export function plainTextToHtml(text: string): string {
  if (!text.trim()) return "";
  const paragraphs = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return paragraphs
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("\n");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Prepara o valor vindo do backend para exibicao no textarea:
 * - se e HTML rico (nao suportado pelo conversor), retorna o HTML cru
 *   (a UI deve avisar o operador que esta editando HTML bruto).
 * - se e HTML simples (so <p>/<br>) ou texto puro, retorna texto legivel.
 */
export function toEditableText(value: string | null | undefined): {
  text: string;
  isRichHtml: boolean;
} {
  const v = value ?? "";
  if (isRichHtml(v)) {
    return { text: v, isRichHtml: true };
  }
  return { text: htmlToPlainText(v), isRichHtml: false };
}

/**
 * Serializa o valor editado pelo operador de volta pro formato salvo.
 * - se estava em modo "HTML rico preservado", salva como digitado (o
 *   operador esta editando HTML bruto conscientemente, avisado pela UI).
 * - caso contrario, converte texto puro -> <p>/<br>.
 */
export function fromEditableText(text: string, wasRichHtml: boolean): string {
  if (wasRichHtml) return text;
  return plainTextToHtml(text);
}
